import { Injectable } from '@angular/core';

/**
 * Style values to inline onto every element that has the given class.
 * These land as element-level inline styles (specificity 1-0-0-0), which
 * is what defeats Bootstrap's `@media print .table th/td { background: #fff !important }`
 * — a stylesheet rule cannot beat inline `!important`.
 */
export interface PrintColorRule {
    /** Background colour, e.g. `#FAAB18`. */
    bg?: string;
    /** Foreground colour, e.g. `#fff`. */
    fg?: string;
}

/**
 * Extra per-selector inline style decorators applied after the colour rules.
 * Use these for things NextPage / Bootstrap does not cover (custom mix-header
 * borders, project-specific spacing, etc.).
 */
export interface PrintInlineDecorator {
    selector: string;
    style: string;
}

export interface PrintOptions {
    /** Map of class name (no leading dot) => colours. */
    colorMap?: Record<string, PrintColorRule>;

    /** Additional per-selector inline style rules. */
    decorators?: PrintInlineDecorator[];

    /** Popup dimensions. Defaults: 900x750. */
    windowWidth?: number;
    windowHeight?: number;

    /** `@page` size, e.g. `A4 landscape` (default) or `A4 portrait`. */
    pageSize?: string;

    /** `@page` margin, e.g. `0mm` (default). */
    pageMargin?: string;

    /** Extra padding applied to the popup `<body>`. Default `8mm`. */
    bodyPadding?: string;

    /**
     * When popups are blocked we fall back to `window.print()` on the host page.
     * This class is added to `document.body` for the duration of that call so
     * component CSS can use a `body.<class>` selector to isolate the print view.
     */
    fallbackBodyClass?: string;

    /** Delay (ms) before triggering print in the popup. Default 400. */
    printDelayMs?: number;
}

/**
 * Reusable client-side print helper.
 *
 * Opens a dedicated popup window, mirrors the target element into it, applies
 * inline style overrides so colours survive the browser's / Bootstrap's print
 * reset, and triggers the browser print dialog on that isolated document.
 *
 * The popup loads whichever NextPage stylesheet is already linked on the host
 * page, so all visual styling comes from NextPage — no CSS is inlined here
 * beyond the minimum print-mechanism rules (`@page`, `print-color-adjust`,
 * body padding).
 */
@Injectable({ providedIn: 'root' })
export class PrintService {
    print(contentEl: HTMLElement | null, options: PrintOptions = {}): void {
        if (!contentEl) return;

        const {
            colorMap = {},
            decorators = [],
            pageSize = 'A4 landscape',
            pageMargin = '0mm',
            bodyPadding = '8mm',
        } = options;

        const clone = contentEl.cloneNode(true) as HTMLElement;

        // 1) Class-based colour rules — inlined so they beat Bootstrap's print
        //    reset of `.table th/td { background-color: #fff !important }`.
        for (const [cls, colors] of Object.entries(colorMap)) {
            clone.querySelectorAll<HTMLElement>('.' + cls).forEach((el) => {
                const parts: string[] = [];
                if (colors.bg) parts.push(`background-color: ${colors.bg} !important`);
                if (colors.fg) parts.push(`color: ${colors.fg} !important`);
                parts.push('-webkit-print-color-adjust: exact !important');
                parts.push('print-color-adjust: exact !important');
                this.appendInlineStyle(el, parts.join('; '));
            });
        }

        // 2) Free-form per-selector decorators.
        for (const { selector, style } of decorators) {
            clone.querySelectorAll<HTMLElement>(selector).forEach((el) => {
                this.appendInlineStyle(el, style);
            });
        }

        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;top:0;left:-9999px;width:900px;height:750px;border:0;';
        document.body.appendChild(iframe);
        const nextPageLinkHtml = this.buildNextPageLinkTag();
        iframe.srcdoc = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title></title>
  ${nextPageLinkHtml}
  <style>
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
    @page { margin: ${pageMargin}; size: ${pageSize}; }
    html, body { margin: 0 !important; padding: ${bodyPadding}; width: 100%; }
  </style>
</head>
<body>
  ${clone.innerHTML}
  <script>
    window.onload = function () {
      window.focus();
      window.print();
      window.onafterprint = function () { window.parent.document.body.removeChild(window.frameElement); };
    };
  </script>
</body>
</html>`;
    }

    /**
     * Locate the NextPage stylesheet already loaded on the host page and
     * reproduce its `<link>` tag (preserving integrity + crossorigin) so the
     * popup uses exactly the same asset.
     */
    private buildNextPageLinkTag(): string {
        const link = document.querySelector<HTMLLinkElement>(
            'link[rel="stylesheet"][href*="NextPage.min.css"]',
        );
        if (!link) return '';

        const attrs = [`rel="stylesheet"`, `href="${link.href}"`];
        if (link.integrity) attrs.push(`integrity="${link.integrity}"`);
        if (link.crossOrigin || link.integrity) attrs.push('crossorigin="anonymous"');
        return `<link ${attrs.join(' ')} />`;
    }

    private appendInlineStyle(el: HTMLElement, addition: string): void {
        const prev = el.getAttribute('style') ?? '';
        const trimmed = prev.trim().replace(/;\s*$/, '');
        el.setAttribute('style', trimmed ? `${trimmed}; ${addition}` : addition);
    }
}
